"use client";

import { useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { WebOnlyNotice } from "@/components/mobile/WebOnlyNotice";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import { Badge } from "@/app/_module/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { Building, CreditCard, Users, DollarSign, User, Settings, Loader2, Crown, Shield, Check, FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { AgencyBillingDashboard } from "./AgencyBillingDashboard";
import { SubscriptionPlans } from "./SubscriptionPlans";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { getEnterpriseCoverageStatus, type EnterpriseCoverageStatus } from "@/app/_module/services/api/agency-billing.api";

interface AgencyBillingSettingsProps {
  agencyId: string;
  currentPolicy?: {
    seatPayor: 'agency' | 'agent';
    plan: 'lite' | 'basic' | 'full_fledged';
    billingInterval: 'monthly' | 'yearly';
  };
}

export const AgencyBillingSettings = function({ agencyId, currentPolicy }: AgencyBillingSettingsProps) {
  const { toast } = useToast();
  const { user } = useUserContext();
  const searchParams = useSearchParams();
  const router = useRouter();

  // Check if user is in an enterprise agency
  const isEnterpriseAgency = (user?.user as any)?.isEnterpriseAgency === true;
  const isEnterpriseAccountType = user?.user?.accountType === 'enterprise';

  // Fetch enterprise coverage status — always enabled so we detect enterprise owners
  // even when accountType is not 'enterprise' (e.g., full_fledged owner of an enterprise agency)
  const { data: enterpriseStatus, isLoading: isLoadingEnterprise } = useQuery<EnterpriseCoverageStatus>({
    queryKey: ['enterprise-coverage-status'],
    queryFn: getEnterpriseCoverageStatus,
    staleTime: 1000 * 60 * 5,
  });

  // Enterprise owner detection — wait for API to resolve for reliable detection
  const isEnterpriseOwner = enterpriseStatus?.isOwner === true || isEnterpriseAccountType;
  // Enterprise member = API explicitly says in hierarchy but NOT owner
  const isEnterpriseMember = enterpriseStatus?.isInEnterpriseHierarchy === true && !enterpriseStatus?.isOwner;
  // Native app: agency billing is web-only (store-review safety).
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [seatPayor, setSeatPayor] = useState<'agency' | 'agent'>(currentPolicy?.seatPayor || 'agent');
  const [plan, setPlan] = useState<'lite' | 'basic' | 'full_fledged'>(currentPolicy?.plan || 'basic');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>(currentPolicy?.billingInterval || 'monthly');
  const [faxIncludedForAgents, setFaxIncludedForAgents] = useState(false);
  const [isSavingFaxSettings, setIsSavingFaxSettings] = useState(false);

  // Get billing sub-tab from URL, default to "my-subscription"
  const billingSubTab = searchParams.get("billingTab") || "my-subscription";

  // Handle billing sub-tab change and update URL
  const handleBillingSubTabChange = (value: string) => {
    router.replace(`/settings?tab=subscription&billingTab=${value}`, { scroll: false });
  };

  // Fetch current billing settings on mount
  useEffect(() => {
    const fetchBillingSettings = async () => {
      if (!agencyId) {
        setIsFetching(false);
        return;
      }

      try {
        const response = await api.get({
          url: getApiUrl(`/agencies/${agencyId}/billing-settings`),
        });

        const result = response as any;
        const data = result?.data || result;

        if (data) {
          // Handle both camelCase and snake_case from backend
          if (data.seatPayor || data.seat_payor) {
            setSeatPayor(data.seatPayor || data.seat_payor);
          }
          if (data.plan) {
            setPlan(data.plan);
          }
          if (data.billingInterval || data.billing_interval) {
            setBillingInterval(data.billingInterval || data.billing_interval);
          }
          if (data.faxIncludedForAgents != null || data.fax_included_for_agents != null) {
            setFaxIncludedForAgents(data.faxIncludedForAgents ?? data.fax_included_for_agents ?? false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch billing settings:', error);
        // Don't show error toast - just use defaults
      } finally {
        setIsFetching(false);
      }
    };

    fetchBillingSettings();
  }, [agencyId]);

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Real API call to save agency billing settings
      const response = await api.put({
        url: getApiUrl(`/agencies/${agencyId}/billing-settings`),
        body: {
          seatPayor,
          plan,
          billingInterval
        }
      });
      
      // Note: axios interceptor returns response.data directly, not the full axios response
      // The response could be: { statusCode: 200, data: {...} } or just { data: {...} }
      // If we get a response without an error, it's a success
      const result = response as any;
      // Check for success: either explicit statusCode 200, or presence of data (no error thrown)
      if (result?.statusCode === 200 || result?.data || (result && !result?.error)) {
        toast({
          title: "Settings Updated",
          description: "Agency billing settings have been saved successfully.",
        });
      } else {
        throw new Error(result?.message || 'Failed to update billing settings');
      }
    } catch (error: any) {
      console.error('Billing settings update error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update billing settings. Please try again.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFaxSettings = async () => {
    setIsSavingFaxSettings(true);
    try {
      const response = await api.put({
        url: getApiUrl(`/agencies/${agencyId}/billing-settings`),
        body: {
          seatPayor,
          plan,
          billingInterval,
          faxIncludedForAgents,
        }
      });

      const result = response as any;
      if (result?.statusCode === 200 || result?.data || (result && !result?.error)) {
        toast({
          title: "Fax Settings Updated",
          description: faxIncludedForAgents
            ? "Agents can now get fax numbers for free."
            : "Agents must now pay $5/month for fax numbers.",
        });
      } else {
        throw new Error(result?.message || 'Failed to update fax settings');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update fax settings.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingFaxSettings(false);
    }
  };

  // Native app: instruction-only card, same as the other billing surfaces.
  if (isNativeApp) {
    return (
      <WebOnlyNotice
        icon={Building}
        title="Agency Billing"
        message="Agency billing and agent seat settings are available on the web version of InsuraCentral. To manage them, log in on your computer at app.insuracentral.com."
      />
    );
  }

  // Wait for enterprise status to resolve before deciding which layout to show
  if (isLoadingEnterprise) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription settings...</p>
        </div>
      </div>
    );
  }

  // Enterprise MEMBERS see regular subscription view (no tabs, just their own plan)
  // Their coverage is managed by the enterprise owner via Agents Billing tab
  if (isEnterpriseMember) {
    return (
      <div className="space-y-6 overflow-hidden">
        <div className="mb-6">
          <h2 className="text-3xl font-bold tracking-tight">Subscription & Billing</h2>
          <p className="text-muted-foreground mt-2">
            Manage your personal subscription plan and billing
          </p>
        </div>
        <SubscriptionPlans />
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-hidden">
      <div className="mb-6">
        <h2 className="text-3xl font-bold tracking-tight">Subscription & Billing</h2>
        <p className="text-muted-foreground mt-2">
          Manage your personal subscription, agency settings, and agent seats
        </p>
      </div>

      <Tabs value={billingSubTab} onValueChange={handleBillingSubTabChange} className="w-full">
        <TabsList className="flex w-full min-w-0 overflow-x-auto gap-1 md:grid md:grid-cols-3 md:justify-center mb-6 scrollbar-hide">
          <TabsTrigger value="my-subscription" className="flex items-center gap-2 flex-shrink-0">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="whitespace-nowrap">My Subscription</span>
          </TabsTrigger>
          <TabsTrigger value="agency-settings" className="flex items-center gap-2 flex-shrink-0">
            <Settings className="h-4 w-4 flex-shrink-0" />
            <span className="whitespace-nowrap">Agency Subscription</span>
          </TabsTrigger>
          <TabsTrigger value="agent-seats" className="flex items-center gap-2 flex-shrink-0">
            <Users className="h-4 w-4 flex-shrink-0" />
            <span className="whitespace-nowrap">Agents Billing</span>
          </TabsTrigger>
        </TabsList>

        {/* My Subscription Tab */}
        <TabsContent value="my-subscription" className="space-y-6">
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Your Personal Subscription</CardTitle>
              <CardDescription>
                {isEnterpriseOwner
                  ? "Your enterprise subscription plan"
                  : "Manage your own subscription plan and billing"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <SubscriptionPlans isEnterpriseOwner={isEnterpriseOwner} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agency Settings Tab */}
        <TabsContent value="agency-settings" className="space-y-6">
          {isEnterpriseOwner ? (
            <>
              {/* Enterprise Agency Plan Overview */}
              <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-purple-600" />
                    Enterprise Agency Plan
                  </CardTitle>
                  <CardDescription>
                    Your organization is on the Enterprise plan with centralized agent coverage
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        <h4 className="font-medium text-gray-900">Plan Status</h4>
                      </div>
                      <Badge className="bg-green-100 text-green-800 border-green-200">Active — Enterprise</Badge>
                      <p className="text-sm text-gray-500 mt-2">
                        Full access to all premium features and agent management.
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="h-5 w-5 text-purple-600" />
                        <h4 className="font-medium text-gray-900">Coverage Model</h4>
                      </div>
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200">Centralized Coverage</Badge>
                      <p className="text-sm text-gray-500 mt-2">
                        You manage which agents are covered via the Agents Billing tab.
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                      <Check className="h-4 w-4" /> Enterprise Capabilities
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-purple-800">
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Cover agents with Basic or Full-Fledged plans</div>
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Manage sub-agencies and their agents</div>
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Centralized billing for all covered seats</div>
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> White-label branding support</div>
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Per-plan seat limits (admin-configured)</div>
                      <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Priority support with SLA</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 border rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600">
                      To add or remove agent coverage, go to the <strong>Agents Billing</strong> tab.
                      For seat limit changes, contact your InsuraCentral account manager.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Fax Number Coverage Toggle */}
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Fax Number Coverage
                  </CardTitle>
                  <CardDescription>
                    Control whether agents get fax numbers for free or must pay $5/month
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div
                    className="flex items-center justify-between cursor-pointer p-4 bg-white rounded-lg border hover:border-purple-300 transition-colors"
                    onClick={() => setFaxIncludedForAgents(!faxIncludedForAgents)}
                  >
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="fax-toggle" className="text-base font-medium cursor-pointer">
                        Include Fax Numbers for Agents
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {faxIncludedForAgents
                          ? "Agents get fax numbers for free — included in their coverage"
                          : "Agents must pay $5/month via Stripe checkout for fax numbers"
                        }
                      </p>
                    </div>
                    <Switch
                      id="fax-toggle"
                      checked={faxIncludedForAgents}
                      onCheckedChange={setFaxIncludedForAgents}
                    />
                  </div>

                  {faxIncludedForAgents ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-green-900">Fax Numbers Included</p>
                          <ul className="text-sm text-green-700 space-y-1">
                            <li>Agents can provision fax numbers for free</li>
                            <li>No additional charge per agent</li>
                            <li>Same experience as non-enterprise users</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CreditCard className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-amber-900">Agents Pay Separately</p>
                          <ul className="text-sm text-amber-700 space-y-1">
                            <li>Agents must subscribe for $5/month via Stripe</li>
                            <li>Each agent pays directly with their own card</li>
                            <li>Agency is not charged for fax numbers</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveFaxSettings}
                      disabled={isSavingFaxSettings}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isSavingFaxSettings ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Fax Settings"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {isFetching ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-muted-foreground">Loading settings...</span>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Seat Payment Model */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Seat Payment Model
                      </CardTitle>
                      <CardDescription>
                        Choose how agent seats are paid for in your agency
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setSeatPayor(seatPayor === 'agency' ? 'agent' : 'agency')}
                      >
                        <div className="space-y-1">
                          <Label htmlFor="seat-payor" className="text-base font-medium cursor-pointer">
                            Agency pays for all agent seats
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {seatPayor === 'agency'
                              ? "Your agency will be charged for all agent subscriptions"
                              : "Agents will pay for their own subscriptions individually"
                            }
                          </p>
                        </div>
                        <Switch
                          id="seat-payor"
                          checked={seatPayor === 'agency'}
                          onCheckedChange={(checked) => setSeatPayor(checked ? 'agency' : 'agent')}
                        />
                      </div>

                      {seatPayor === 'agency' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <DollarSign className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-blue-900">
                                Agency Payment Benefits
                              </p>
                              <ul className="text-sm text-blue-700 space-y-1">
                                <li>• Simplified billing - one invoice for all agents</li>
                                <li>• Centralized payment management</li>
                                <li>• Manual control over individual agent plans</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Agency Plan Selection */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building className="h-5 w-5" />
                        Agency Plan
                      </CardTitle>
                      <CardDescription>
                        Select the plan for your agency account <span className="text-red-600">(price per agent)</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            plan === 'lite'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPlan('lite')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold">Lite Plan</h3>
                            {plan === 'lite' && <Badge>Selected</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            The CRM without the dialers and calling: no dialer, number or texting
                          </p>
                          <div className="text-2xl font-bold">
                            $60
                            <span className="text-sm font-normal text-muted-foreground">
                              {" "}per month
                            </span>
                          </div>
                        </div>
                        <div
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            plan === 'basic'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPlan('basic')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold">Basic Plan</h3>
                            {plan === 'basic' && <Badge>Selected</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Essential features for small agencies
                          </p>
                          <div className="text-2xl font-bold">
                            $120
                            <span className="text-sm font-normal text-muted-foreground">
                              {" "}per month
                            </span>
                          </div>
                        </div>

                        <div
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            plan === 'full_fledged'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPlan('full_fledged')}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold">Full Fledged Plan</h3>
                            {plan === 'full_fledged' && <Badge>Selected</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            Advanced features for growing agencies
                          </p>
                          <div className="text-2xl font-bold">
                            $240
                            <span className="text-sm font-normal text-muted-foreground">
                              {" "}per month
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveSettings}
                      disabled={isLoading}
                      className="bg-primary hover:bg-primary/90"
                    >
                      {isLoading ? "Saving..." : "Save Settings"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* Agent Seats Tab */}
        <TabsContent value="agent-seats" className="space-y-6">
          {isEnterpriseOwner ? (
            <AgencyBillingDashboard isEnterpriseOwner={isEnterpriseOwner} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Agent Billing Management
                </CardTitle>
                <CardDescription>
                  Manage subscriptions and payment sources for all agents in your agency
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AgencyBillingDashboard isEnterpriseOwner={false} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
